// Meta Embedded Signup — Facebook JS SDK'yı yükler, WA_EMBEDDED_SIGNUP mesajını
// dinler ve FB.login ile yetki kodunu alır (spec 2026-10-06 › Akışlar › Bağlama).
// `coexistence: true` → Meta penceresi "mevcut WhatsApp Business uygulamasını bağla"
// akışıyla açılır (featureType). Bu akış bitişte yalnız waba_id döndürür; numara
// kimliğini sunucu WABA'dan okur.

import { useCallback, useEffect, useRef } from "react";

const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const SDK_ID = "facebook-jssdk";
const IDS_WAIT_MS = 3000;
const COEXISTENCE_FEATURE = "whatsapp_business_app_onboarding";
const FINISH_EVENTS = ["FINISH", "FINISH_ONLY_WABA", "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"];

let sdkPromise = null;
let initializedAppId = null;

/** SDK'yı bir kez yükler; FB nesnesiyle çözülür. */
function loadSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("no-window"));
  if (window.FB) return Promise.resolve(window.FB);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const prev = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      if (typeof prev === "function") prev();
      resolve(window.FB);
    };
    if (!document.getElementById(SDK_ID)) {
      const s = document.createElement("script");
      s.id = SDK_ID;
      s.src = SDK_SRC;
      s.async = true;
      s.defer = true;
      s.crossOrigin = "anonymous";
      s.onerror = () => {
        sdkPromise = null;
        reject(new Error("sdk-load-failed"));
      };
      document.body.appendChild(s);
    }
  });
  return sdkPromise;
}

const isFacebookOrigin = (origin) => {
  try {
    const host = new window.URL(origin).hostname;
    return host === "facebook.com" || host.endsWith(".facebook.com");
  } catch (_e) {
    return false;
  }
};

/**
 * @returns {{ start: (cfg: {appId, configId, graphVersion, coexistence?}) => Promise<
 *   {status: "code", code, wabaId, phoneNumberId, coexistence} | {status: "cancel"} | {status: "error", reason}>,
 *   abort: () => void
 * }}
 */
export default function useEmbeddedSignup() {
  const idsRef = useRef({ wabaId: null, phoneNumberId: null, finished: false, cancelled: false });
  // Meta penceresi hata sayfasıyla kapanınca (ör. Safari) FB.login geri çağırmayabilir;
  // kullanıcı "Vazgeç" ile beklemeyi bitirir.
  const abortRef = useRef(null);

  // SDK önceden yüklenir: tıklamada FB.login senkron çağrılsın, Safari pencereyi engellemesin.
  useEffect(() => {
    loadSdk().catch(() => {});
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (!isFacebookOrigin(event.origin)) return;
      let data;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch (_e) {
        return;
      }
      if (!data || data.type !== "WA_EMBEDDED_SIGNUP") return;
      if (FINISH_EVENTS.includes(data.event)) {
        idsRef.current = {
          wabaId: data.data?.waba_id || null,
          phoneNumberId: data.data?.phone_number_id || null,
          finished: true,
          cancelled: false,
        };
      } else if (data.event === "CANCEL") {
        idsRef.current = { ...idsRef.current, cancelled: true };
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const waitForIds = () =>
    new Promise((resolve) => {
      const started = Date.now();
      const tick = () => {
        const ids = idsRef.current;
        // Coexistence bitişinde phone_number_id gelmez — waba_id + bitiş olayı yeter
        const ready = ids.wabaId && (ids.phoneNumberId || ids.finished);
        if (ready || Date.now() - started >= IDS_WAIT_MS) return resolve(ids);
        setTimeout(tick, 150);
      };
      tick();
    });

  const start = useCallback(async ({ appId, configId, graphVersion, coexistence = false }) => {
    idsRef.current = { wabaId: null, phoneNumberId: null, finished: false, cancelled: false };
    let FB = window.FB;
    if (!FB) {
      try {
        FB = await loadSdk();
      } catch (_e) {
        return { status: "error", reason: "sdk" };
      }
    }
    if (initializedAppId !== appId) {
      FB.init({ appId, autoLogAppEvents: true, xfbml: true, version: graphVersion });
      initializedAppId = appId;
    }
    const response = await new Promise((resolve) => {
      abortRef.current = () => resolve({ aborted: true });
      FB.login((res) => resolve(res), {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras: coexistence
          ? { setup: {}, featureType: COEXISTENCE_FEATURE, sessionInfoVersion: "3" }
          : { setup: {}, sessionInfoVersion: "3" },
      });
    });
    abortRef.current = null;
    if (response?.aborted) return { status: "cancel" };
    const code = response?.authResponse?.code;
    if (!code) {
      return idsRef.current.cancelled || response?.status !== "connected"
        ? { status: "cancel" }
        : { status: "error", reason: "no-code" };
    }
    const ids = await waitForIds();
    return { status: "code", code, wabaId: ids.wabaId, phoneNumberId: ids.phoneNumberId, coexistence };
  }, []);

  const abort = useCallback(() => abortRef.current?.(), []);

  return { start, abort };
}
